import { LG_COLOR } from 'containers/App/constants';
import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Button from 'reusable/Button';
import Table from 'reusable/Table';
import Tooltip from 'reusable/Tooltip';
import { useFetchRemoteLayoutQuery } from 'services/api/remoteLayout';
import { RemoteLayoutStyles } from './style';
import { IconButton } from 'rsuite';
import { FaClone, FaEdit, FaLink, FaTrash } from 'react-icons/fa';
import LayoutForm from './components/LayoutForm';
import DeleteModal from './components/DeleteModal';
import LinkModal from './components/LinkModal';
import useHeader from 'containers/App/Header/useHeader';

const UserInfoDisplay = ({ value }) =>
  value ? (
    <Tooltip
      tooltipValue={
        <>
          {get(value, 'name')}
          <br />
          {get(value, 'email')}
        </>
      }
    >
      <div>{get(value, 'name', '')}</div>
    </Tooltip>
  ) : null;

const DateDisplay = ({ value }) => {
  const date = new Date(value);
  return (
    <Tooltip tooltipValue={date.toUTCString()}>
      <div>{date.toDateString()}</div>
    </Tooltip>
  );
};

function RemoteLayouts() {
  useHeader('Remote Configuration');

  const [showModal, setShowModal] = useState({} as any);
  const [deleteModal, setDeleteModal] = useState({} as any);
  const [linkModal, setLinkModal] = useState({} as any);

  const {
    data: remoteLayouts = [],
    isLoading,
    isFetching,
    isError,
    error,
  } = useFetchRemoteLayoutQuery('');

  const columns = useMemo(
    () => [
      {
        Header: 'Id',
        accessor: 'id',
        className: 'id',
        disableFilters: true,
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: 'Name',
        accessor: 'name',
        className: 'name',
      },
      {
        Header: 'Created',
        accessor: 'created',
        className: 'created',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <div>
            <UserInfoDisplay value={get(row, 'original.metadata.createdBy')} />
            <DateDisplay value={get(row, 'original.metadata.createdOn')} />
          </div>
        ),
      },
      {
        Header: 'Last Modified',
        accessor: 'modified',
        className: 'modified',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <div>
            <UserInfoDisplay
              value={get(row, 'original.metadata.lastModifiedBy')}
            />
            <DateDisplay value={get(row, 'original.metadata.lastModifiedOn')} />
          </div>
        ),
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        className: 'actions',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <>
            <IconButton
              circle
              icon={<FaClone />}
              onClick={() =>
                setShowModal({
                  show: true,
                  action: 'clone',
                  data: row.original,
                })
              }
            />
            <IconButton
              circle
              icon={<FaEdit />}
              onClick={() =>
                setShowModal({
                  show: true,
                  action: 'edit',
                  data: row.original,
                })
              }
            />
            {row.original.name !== 'default' && (
              <IconButton
                circle
                icon={<FaTrash />}
                onClick={() =>
                  setDeleteModal({
                    show: true,
                    name: row.original.name,
                  })
                }
              />
            )}
            <IconButton
              circle
              icon={<FaLink />}
              onClick={() =>
                setLinkModal({ show: true, name: row.original.name })
              }
            />
          </>
        ),
      },
    ],
    [],
  );

  useEffect(() => {
    if (isError) toast.error(get(error, 'data.error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  return (
    <RemoteLayoutStyles>
      <div className="remote-layout-shell">
        <div className="remote-layout-header">
          <div>
            <div className="remote-layout-eyebrow">Admin Center</div>
            <h1 className="remote-layout-title">Remote Configuration</h1>
            <p className="remote-layout-subtitle">
              Create, clone, link, and maintain the reusable remote layouts that
              power pod control surfaces.
            </p>
          </div>
          <div className="remote-layout-metric">
            <div className="remote-layout-metric-label">Layouts</div>
            <div className="remote-layout-metric-value">
              {remoteLayouts.length}
            </div>
          </div>
        </div>

        <div className="remote-layout-panel">
          <div className="remote-layout-panel-header">
            <div>
              <h2 className="remote-layout-panel-title">Configured layouts</h2>
              <p className="remote-layout-panel-subtitle">
                Filter by name, review ownership, and manage pod mappings.
              </p>
            </div>
            <Button
              color={LG_COLOR}
              inverted="true"
              className="remote-layout-add-button"
              onClick={() => setShowModal({ show: true, action: 'new' })}
            >
              Add Remote Layout
            </Button>
          </div>

          {isError ? (
            <Alert className="remote-layout-alert" variant="danger">
              {get(error, 'data.error') || 'Failed to load remote layouts.'}
            </Alert>
          ) : null}

          {!isLoading && !isFetching && !remoteLayouts.length ? (
            <div className="remote-layout-empty-state">
              No remote layouts have been configured yet.
            </div>
          ) : (
            <Table
              columns={columns}
              data={remoteLayouts}
              className="remote-layout-table"
              sortable
              filterable
              loading={isLoading || isFetching ? 'true' : undefined}
            />
          )}
        </div>
      </div>

      <LayoutForm
        action={showModal.action}
        show={showModal.show}
        onHide={() => setShowModal({ show: false })}
        data={showModal.data || {}}
      />

      <DeleteModal
        show={deleteModal.show}
        onHide={() => setDeleteModal({})}
        name={deleteModal.name}
      />

      <LinkModal
        show={linkModal.show}
        name={linkModal.name}
        onHide={() => setLinkModal({})}
      />
    </RemoteLayoutStyles>
  );
}

export default RemoteLayouts;
